import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Avatar,
    Divider,
    IconButton,
    Drawer,
    TextField,
    InputAdornment,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { connect, useDispatch } from 'react-redux';
import { EMPLOYEES } from '../redux.datasets';
import { DOMAIN_NAME } from '../common';
import createCachedSelector from 're-reselect';
import { entitySelector } from '@libs/datasets';
import { updateManager } from 'managerialTree/redux.actions';
import { cardColor } from 'managerialTree/colorVariant';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import {
    isAncestor,
    isDescendant,
    matchesSearch
} from 'managerialTree/utils/commonHelper';
import { useNavigationState } from '@libComponents/NavigationContainer/useNavigation';
import { useTheme } from '@mui/material/styles';

const dataSelector = createCachedSelector(entitySelector, (resp) =>
    resp && resp.data
        ? resp.data.map((obj) => {
              let data = (({ user, branch, designation, ...rest }) => ({
                  ...rest,
                  email: user?.email,
                  branchName: branch?.name,
                  designationName: designation?.name
              }))(obj);

              return {
                  ...data
              };
          })
        : []
)((state, domain, entity) => `@data-${domain}-${entity}`);

const mapState = (state) => ({
    data: dataSelector(state, DOMAIN_NAME, EMPLOYEES)
});

const EmployeeModal = ({
    empTree,
    toggleDrawer,
    selectedNodeData,
    open,
    data,
    afterManagerUpdateHandler
}) => {
    const RXDispatch = useDispatch();
    const navigate = useNavigate();
    const [showManagerSearch, setShowManagerSearch] = useState(false);
    const [showTeamSearch, setShowTeamSearch] = useState(false);
    const [filteredName, setFilteredName] = useState('');

    // Confirmation modal state
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    let { flatHashes } = useNavigationState();
    const canSeeAllEmployee = flatHashes['/employee/all-employee']
        ? true
        : false;

    const theme = useTheme();

    let selectedEmployee = data.find((el) => el.email === selectedNodeData);
    let reportingEmployee = selectedEmployee.reportingManager
        ? data.find((el) => el.email === selectedEmployee.reportingManager)
        : null;
    let teamMembers = data.filter((el) => el.reportingManager === selectedEmployee.email);
    const departmentColor = (person) => {
        const raw = cardColor[person.department.name] || cardColor['Staff'];
        if (theme.palette.mode === 'dark') {
            return {
                background: raw.darkBackground || raw.background,
                border: raw.darkBorder || raw.border,
                toogle: raw.darkToogle || raw.toogle,
                view: raw.darkView || raw.view
            };
        }
        return raw;
    };

    const renderPerson = (keyName, person) => (
        <Box display='flex' alignItems='center' mb={1.5}>
            <Avatar sx={{ bgcolor: departmentColor(person).border, mr: 2 }}>
                {person?.firstname?.charAt(0).toUpperCase()}
            </Avatar>
            <Box
                display='flex'
                justifyContent='space-between'
                width='100%'
                alignItems='center'
            >
                <Box>
                    <Typography fontWeight='bold'>
                        {person.firstname + ' ' + person.lastname}
                    </Typography>
                    <Typography variant='body2'>
                        {person.designationName}
                    </Typography>
                </Box>
                <Box marginLeft='auto'>
                    <Typography
                        variant='caption'
                        color='text.secondary'
                        fontSize='1rem'
                    >
                        {person.email}
                    </Typography>
                </Box>
                {keyName === 'Team Members' && (
                    <Box>
                        {canSeeAllEmployee  &&  <IconButton
                            onClick={() =>
                                handleEmployeeClick(person, 'deleteSubordinate')
                            }
                        >
                            <DeleteForeverIcon
                                fontSize='small'
                                sx={{ color: 'error.main' }}
                            /> 
                        </IconButton> }
                    </Box>

                )}
            </Box>
        </Box>
    );

    const handleReportingAssign = (emp, reportingManager) => {
        RXDispatch(
            updateManager({
                handleSubmit: () => afterManagerUpdateHandler(),
                emp,
                reportingManager
            })
        );
    };

    // Handle confirmation dialog
    const handleConfirmationOpen = (
        actionType,
        emp,
        reportingManager,
        actionDescription
    ) => {
        setPendingAction({
            type: actionType,
            emp,
            reportingManager,
            description: actionDescription
        });
        setConfirmationOpen(true);
    };

    const handleConfirmationClose = () => {
        setConfirmationOpen(false);
        setPendingAction(null);
    };

    const handleConfirmAction = () => {
        if (pendingAction) {
            handleReportingAssign(
                pendingAction.emp,
                pendingAction.reportingManager
            );
            // Close search dropdowns
            setShowManagerSearch(false);
            setShowTeamSearch(false);
            setFilteredName('');
        }
        handleConfirmationClose();
    };

    const getActionDescription = (
        actionType,
        selectedEmployee,
        targetEmployee
    ) => {
        if (actionType === 'manager') {
            return selectedEmployee.reportingManager
                ? `Change ${selectedEmployee.firstname} ${selectedEmployee.lastname}'s reporting manager to ${targetEmployee.firstname} ${targetEmployee.lastname}?`
                : `Assign ${targetEmployee.firstname} ${targetEmployee.lastname} as reporting manager for ${selectedEmployee.firstname} ${selectedEmployee.lastname}?`;
        } else if (actionType === 'subordinate') {
            return `Add ${targetEmployee.firstname} ${targetEmployee.lastname} to ${selectedEmployee.firstname} ${selectedEmployee.lastname}'s team?`;
        } else {
            return `Delete ${targetEmployee.firstname} ${targetEmployee.lastname} from ${selectedEmployee.firstname} ${selectedEmployee.lastname}'s team?`;
        }
    };

    const handleEmployeeClick = (emp, keyName) => {
        const targetEmployee = data.find((e) => e.email === emp.email);
        const actionDescription = getActionDescription(
            keyName,
            selectedEmployee,
            targetEmployee
        );

        if (keyName === 'manager') {
            const circular = isDescendant(
                empTree,
                selectedEmployee.email,
                emp.email
            );
            if (circular) {
                // Show custom error modal
                setPendingAction({
                    type: 'invalid',
                    emp: null,
                    reportingManager: null,
                    description: `Cannot assign ${emp.firstname} ${emp.lastname} as manager of ${selectedEmployee.firstname} ${selectedEmployee.lastname}`
                });
                setConfirmationOpen(true);
            } else {
                handleConfirmationOpen(
                    'manager',
                    selectedEmployee.email,
                    emp.email,
                    actionDescription
                );
            }
        } else if (keyName === 'subordinate') {
            const circular = isAncestor(
                empTree,
                emp.email,
                selectedEmployee.email
            );
            if (circular) {
                setPendingAction({
                    type: 'invalid',
                    emp: null,
                    reportingManager: null,
                    description: `Cannot assign ${emp.firstname} ${emp.lastname} as a subordinate of ${selectedEmployee.firstname} ${selectedEmployee.lastname}`
                });
                setConfirmationOpen(true);
            } else {
                handleConfirmationOpen(
                    'subordinate',
                    emp.email,
                    selectedEmployee.email,
                    actionDescription
                );
            }
        } else {
            handleConfirmationOpen(
                'deleteSubordinate',
                emp.email,
                null,
                actionDescription
            );
        }
    };

    const renderSectionHeader = (title, keyName, showSearch, toggleSearch) => {
        const handleAddButtonClick = (toggleFunc) => {
            setFilteredName('');
            toggleFunc();
            keyName === 'manager' ? setShowTeamSearch(false) : setShowManagerSearch(false);
        };
        return (
            <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
            >
                <Typography fontWeight='bold'>{title}</Typography>
                {showSearch ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', py: 1, position: 'relative' }}>
                        <TextField
                            size='small'
                            placeholder='Search...'
                            variant='outlined'
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <IconButton onClick={toggleSearch}>
                                            <CloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ width: 240 }}
                            onChange={(e) => setFilteredName(e.target.value)}
                        />
                        <Box sx={{ position: 'absolute', right: 0, top: 50, zIndex: 9, width: 500, maxHeight: '20rem', bgcolor: 'background.paper', px: 0.5, mt: '6px', borderRadius: 1, boxShadow: 4, overflow: 'auto' }}>
                            {filteredName.length > 1 &&
                                data
                                    .filter((el) =>
                                        matchesSearch(
                                            el,
                                            filteredName,
                                            selectedEmployee.email
                                        )
                                    )
                                    .map((emp, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                borderTop: 1,
                                                borderColor: 'divider',
                                                p: 2,
                                                my: 0.5,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    bgcolor: 'action.hover',
                                                    borderRadius: 1
                                                }
                                            }}
                                            onClick={() => handleEmployeeClick(emp, keyName)}
                                        >
                                            <Avatar
                                                sx={{
                                                    bgcolor:
                                                        departmentColor(emp)
                                                            .border,
                                                    width: 24,
                                                    height: 24,
                                                    fontSize: '16px',
                                                    mr: 2
                                                }}
                                            >
                                                {emp?.firstname
                                                    ?.charAt(0)
                                                    .toUpperCase()}
                                            </Avatar>
                                            <Box sx={{ flex: '1 1 60%' }}>
                                                <Typography fontWeight='bold'>
                                                    {emp.firstname +
                                                        ' ' +
                                                        emp.middlename +
                                                        ' ' +
                                                        emp.lastname}
                                                </Typography>
                                                <Typography variant='body2'>
                                                    {emp.designationName}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: '1 1 40%' }}>
                                                <Typography variant='body2'>
                                                    {emp.email}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                        </Box>
                    </Box>
                ) : (
                    canSeeAllEmployee && (
                        <Button
                            size='small'
                            startIcon={<AddIcon />}
                            variant='outlined'
                            onClick={() => handleAddButtonClick(toggleSearch)}
                        >
                                                        {keyName === 'manager'
                                                                ? (selectedEmployee.reportingManager === null || selectedEmployee.reportingManager === '' ? 'Add' : 'Update')
                                                                : 'Add'}
                        </Button>
                    )
                )}
            </Box>
        );
    };

    // Confirmation Dialog Component
    const ConfirmationDialog = () => (
        <Dialog
            open={confirmationOpen}
            onClose={handleConfirmationClose}
            aria-labelledby='confirmation-dialog-title'
            aria-describedby='confirmation-dialog-description'
        >
            <DialogTitle id='confirmation-dialog-title'>
                {pendingAction?.type === 'invalid'
                    ? 'Invalid Action'
                    : 'Confirm Action'}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id='confirmation-dialog-description'>
                    {pendingAction?.description}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleConfirmationClose} color='primary'>
                    {pendingAction?.type === 'invalid' ? 'OK' : 'Cancel'}
                </Button>
                {pendingAction?.type !== 'invalid' && (
                    <Button
                        onClick={handleConfirmAction}
                        color='primary'
                        variant='contained'
                    >
                        Confirm
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );

    return (
        <>
            <Drawer open={open} onClose={toggleDrawer(false)} anchor={'right'}>
                <Box
                    width={593}
                    role='presentation'
                    height={'100vh'}
                    display={'flex'}
                    flexDirection={'column'}
                >
                    <Box display='flex' justifyContent='space-between' p={1}>
                        <Typography
                            variant='h6'
                            component='h2'
                            sx={{ marginLeft: '10px' }}
                        >
                            Employee Information
                        </Typography>
                        <IconButton onClick={toggleDrawer(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Card sx={{ width: '100%', borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <CardContent sx={{ padding: 3 }}>
                            {/* Header Section */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 2
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor:
                                                departmentColor(
                                                    selectedEmployee
                                                ).border,
                                            width: 50,
                                            height: 50,
                                            mx: 'auto'
                                        }}
                                    >
                                        {selectedEmployee?.firstname
                                            ?.charAt(0)
                                            .toUpperCase()}
                                    </Avatar>

                                    <Box>
                                            <Typography variant='h6' component='h2' sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                {selectedEmployee.firstname + ' ' + selectedEmployee.lastname}
                                            </Typography>
                                            <Typography variant='body2' color='text.secondary'>
                                                {selectedEmployee.designationName}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {canSeeAllEmployee && (
                                        <Button
                                            variant='outlined'
                                            size='small'
                                            startIcon={<VisibilityIcon />}
                                            onClick={() => navigate(`/profile?id=${selectedEmployee?.email}`)}
                                            sx={{
                                                borderColor: 'warning.main',
                                                color: 'warning.main',
                                                '&:hover': {
                                                    borderColor: 'warning.dark',
                                                    backgroundColor: 'warning.light'
                                                }
                                            }}
                                        >
                                            View Profile
                                        </Button>
                                    )}
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            {/* Information Section */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{ fontWeight: 500 }}
                                    >
                                        Email
                                    </Typography>
                                    <Typography variant='body2' sx={{ color: 'text.primary' }}>
                                        {selectedEmployee.email}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{ fontWeight: 500 }}
                                    >
                                        Joining Date
                                    </Typography>
                                    <Typography variant='body2' sx={{ color: 'text.primary' }}>
                                        {dayjs(
                                            selectedEmployee.joiningDate
                                        ).format('YYYY-MM-DD')}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{ fontWeight: 500 }}
                                    >
                                        Department
                                    </Typography>
                                    <Typography variant='body2' sx={{ color: 'text.primary' }}>
                                        {selectedEmployee.department.name}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Divider />

                    <Box py={2} px={2}>
                        {renderSectionHeader(
                            'Reporting Manager',
                            'manager',
                            showManagerSearch,
                            () => setShowManagerSearch(!showManagerSearch)
                        )}
                        <Box mt={1}>
                            {reportingEmployee &&
                                renderPerson(
                                    'Reporting Manager',
                                    reportingEmployee
                                )}
                        </Box>
                    </Box>
                    <Divider />
                    <Box py={2} px={2} sx={{ flex: '1', overflow: 'auto' }}>
                        {renderSectionHeader(
                            'Team Members',
                            'subordinate',
                            showTeamSearch,
                            () => setShowTeamSearch(!showTeamSearch)
                        )}
                        <Box mt={1} sx={{ height: '90%', overflow: 'auto' }}>
                            {teamMembers.map((member, idx) => (
                                <Box key={idx}>
                                    {renderPerson('Team Members', member)}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Drawer>

            {/* Confirmation Dialog */}
            <ConfirmationDialog />
        </>
    );
};

export default connect(mapState)(EmployeeModal);
